import * as React from "react";

/**
 * Form primitives, ported from ScadsysIdentity's FormGroup/Label/Input.
 *
 * The reference packs checkbox, radio, colour, textarea, select and text into one polymorphic
 * `Input` that branches on `type`. That is convenient at the call site but loses prop typing, so the
 * branches are split into named components here; `Input` keeps the plain text-ish types. There is no
 * form library in either app — state is one `useState` per field and validation is inline.
 */

const FIELD =
    "block w-full rounded-input border-[1.5px] border-border-strong bg-surface px-3 py-2 text-sm text-text " +
    "placeholder:text-text-weak transition-colors duration-150 ease-out focus:border-primary focus:outline-none " +
    "focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60";

const FIELD_ERROR = "border-danger focus:border-danger focus-visible:ring-danger";

export const FieldError: React.FC<{ children?: React.ReactNode }> = ({ children }) =>
    children ? <p className="mt-1 text-sm text-danger-text">{children}</p> : null;

export const FormGroup: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
    <div className={`mb-4 ${className}`}>{children}</div>
);

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
    required?: boolean;
    children: React.ReactNode;
}

export const Label: React.FC<LabelProps> = ({ children, required, className = "", ...props }) => (
    <label className={`mb-1 block text-sm font-medium text-text ${className}`} {...props}>
        {children}
        {required && <span className="ml-1 text-danger">*</span>}
    </label>
);

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    error?: string;
}

export const Input: React.FC<InputProps> = ({ error, className = "", type = "text", ...props }) => (
    <div>
        <input type={type} className={`${FIELD} ${error ? FIELD_ERROR : ""} ${className}`} aria-invalid={!!error || undefined} {...props} />
        <FieldError>{error}</FieldError>
    </div>
);

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    error?: string;
}

export const Textarea: React.FC<TextareaProps> = ({ error, className = "", rows = 3, ...props }) => (
    <div>
        <textarea rows={rows} className={`${FIELD} ${error ? FIELD_ERROR : ""} ${className}`} aria-invalid={!!error || undefined} {...props} />
        <FieldError>{error}</FieldError>
    </div>
);

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    error?: string;
    /** Rendered as an empty first option so an unset select reads as a prompt, not as its first real value. */
    placeholder?: string;
}

export const Select: React.FC<SelectProps> = ({ error, className = "", placeholder, children, ...props }) => (
    <div>
        <select className={`${FIELD} ${error ? FIELD_ERROR : ""} ${className}`} aria-invalid={!!error || undefined} {...props}>
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {children}
        </select>
        <FieldError>{error}</FieldError>
    </div>
);

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
    label: React.ReactNode;
    hint?: React.ReactNode;
}

export const Checkbox: React.FC<CheckboxProps> = ({ label, hint, className = "", id, ...props }) => {
    const generated = React.useId();
    const inputId = id ?? generated;

    return (
        <div className={`flex items-start gap-2 ${className}`}>
            <input
                id={inputId}
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded-xs border-[1.5px] border-border-strong text-primary focus:ring-ring"
                {...props}
            />
            <label htmlFor={inputId} className="text-sm text-text">
                {label}
                {hint && <span className="block text-xs text-text-weak">{hint}</span>}
            </label>
        </div>
    );
};

/** Label + control + hint, for the common single-field case. */
export interface FieldProps {
    label: React.ReactNode;
    required?: boolean;
    hint?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}

export const Field: React.FC<FieldProps> = ({ label, required, hint, children, className = "" }) => (
    <FormGroup className={className}>
        <Label required={required}>{label}</Label>
        {children}
        {hint && <p className="mt-1 text-xs text-text-weak">{hint}</p>}
    </FormGroup>
);
